export type SectionLayout = "cards" | "list" | "pills" | "links";

export interface ScopeSection {
  key: string;
  title: string;
  layout?: SectionLayout;
  collapsed?: boolean;
}

export interface ScopeIndexEntry {
  key: string;
  title: string;
  desc: string;
  kind: string;
  section?: string;
  tags?: string[];
}

export interface ScopeIndex {
  scope: string;
  summary: string;
  updatedAt: string;
  sections?: ScopeSection[];
  entries: ScopeIndexEntry[];
}

export interface SectionGroup {
  section: ScopeSection;
  entries: ScopeIndexEntry[];
}

function normalizeKey(key: string): string {
  return key.replace(/^\.\//, "");
}

function normalizeEntry(entry: ScopeIndexEntry): ScopeIndexEntry {
  return { ...entry, key: normalizeKey(entry.key) };
}

export function isLinkKind(kind: string): boolean {
  return kind === "jira" || kind === "external" || kind === "scope";
}

function sectionLayout(section: ScopeSection): SectionLayout {
  return section.layout ?? "list";
}

/** Pick a key for a trailing synthetic links section without colliding with existing sections. */
function syntheticLinksKey(usedKeys: Set<string>): string {
  if (!usedKeys.has("links")) return "links";
  if (!usedKeys.has("__links")) return "__links";
  let n = 2;
  while (usedKeys.has(`__links${n}`)) n++;
  return `__links${n}`;
}

export function resolveSectionGroups(index: ScopeIndex | null, fallbackDocs: string[]): SectionGroup[] {
  // Rule 1: No index → synthetic "pages" group from file listing.
  if (!index) {
    const entries = fallbackDocs.map((rel) => {
      const key = normalizeKey(rel);
      return { key, title: key, desc: "", kind: "doc" };
    });
    if (entries.length === 0) return [];
    return [{ section: { key: "pages", title: "Pages", layout: "list" }, entries }];
  }

  const entries = index.entries.map(normalizeEntry);

  // Rule 2: Index without sections → pages + links buckets by kind.
  if (!index.sections || index.sections.length === 0) {
    const pagesSection: ScopeSection = { key: "pages", title: "Pages", layout: "list" };
    const linksSection: ScopeSection = { key: "links", title: "Links", layout: "links" };
    const pagesEntries: ScopeIndexEntry[] = [];
    const linksEntries: ScopeIndexEntry[] = [];
    for (const entry of entries) {
      if (isLinkKind(entry.kind)) linksEntries.push(entry);
      else pagesEntries.push(entry);
    }
    const groups: SectionGroup[] = [];
    if (pagesEntries.length) groups.push({ section: pagesSection, entries: pagesEntries });
    if (linksEntries.length) groups.push({ section: linksSection, entries: linksEntries });
    return groups;
  }

  // Rule 3: sections present → bucket by section key; orphans fall back by kind.
  const sections: ScopeSection[] = index.sections.map((s) => ({
    ...s,
    layout: sectionLayout(s),
  }));
  const sectionKeys = new Set(sections.map((s) => s.key));
  const buckets = new Map<string, ScopeIndexEntry[]>();
  for (const s of sections) buckets.set(s.key, []);

  let linksSectionKey = sections.find((s) => sectionLayout(s) === "links")?.key ?? null;
  let syntheticLinksKeyValue: string | null = null;

  let catchAllKey = sections[sections.length - 1]?.key ?? "pages";
  for (let i = sections.length - 1; i >= 0; i--) {
    if (sectionLayout(sections[i]) !== "links") {
      catchAllKey = sections[i].key;
      break;
    }
  }

  for (const entry of entries) {
    const target = entry.section;
    if (target && sectionKeys.has(target)) {
      buckets.get(target)!.push(entry);
    } else if (isLinkKind(entry.kind)) {
      if (!linksSectionKey) {
        syntheticLinksKeyValue = syntheticLinksKey(sectionKeys);
        linksSectionKey = syntheticLinksKeyValue;
        buckets.set(syntheticLinksKeyValue, []);
      }
      buckets.get(linksSectionKey)!.push(entry);
    } else {
      buckets.get(catchAllKey)!.push(entry);
    }
  }

  const orderedSections = syntheticLinksKeyValue
    ? [...sections, { key: syntheticLinksKeyValue, title: "Links", layout: "links" as const }]
    : sections;

  // Rule 4: Drop sections with zero entries; preserve entry order within buckets.
  return orderedSections
    .filter((s) => (buckets.get(s.key)?.length ?? 0) > 0)
    .map((s) => ({ section: s, entries: buckets.get(s.key)! }));
}
